/**
 * Metabolic Quiz - Scoring Algorithm
 * Calculates user's metabolic profile based on quiz responses
 *
 * SCORING LOGIC:
 * 1. Calculate total scores for each profile (Hépatique, Lymphatique, Lipidique)
 * 2. Determine primary profile (highest score, with tie-breaker priority)
 * 3. Calculate recommended duration based on primary profile score
 */

(function() {
  'use strict';

  /**
   * Calculate metabolic profile from quiz responses
   * @param {Array} responses - Array of user responses with weights
   * @returns {Object} Profile result with primary profile, scores, persona, and result URL
   */
  window.calculateProfile = function(responses) {
    if (!responses || responses.length === 0) {
      console.error('No responses provided for scoring');
      return getDefaultResult();
    }

    // Initialize scores for each profile
    const scores = {
      hepatic: 0,
      lymphatic: 0,
      lipid: 0
    };

    // Group responses by question for multiple choice capping
    const responsesByQuestion = {};
    let persona = null;

    responses.forEach(response => {
      const qId = response.questionId;

      // Extract persona from Q10 (don't include in metabolic scoring)
      if (response.persona) {
        persona = response.persona;
        return; // Skip Q10 in metabolic scoring
      }

      // Group responses by question
      if (!responsesByQuestion[qId]) {
        responsesByQuestion[qId] = [];
      }
      responsesByQuestion[qId].push(response);
    });

    // Calculate scores with capping for multiple choice questions
    // Q3, Q4, Q6, Q8 are multiple choice and capped at 2 points max
    const multipleChoiceQuestions = [3, 4, 6, 8];

    Object.keys(responsesByQuestion).forEach(qId => {
      const qResponses = responsesByQuestion[qId];
      const isMultipleChoice = multipleChoiceQuestions.includes(parseInt(qId));

      if (isMultipleChoice) {
        // Calculate total weights for this multiple choice question
        const qScores = { hepatic: 0, lymphatic: 0, lipid: 0 };

        qResponses.forEach(response => {
          if (response.weights) {
            Object.keys(response.weights).forEach(profile => {
              qScores[profile] += response.weights[profile] || 0;
            });
          }
        });

        // Cap each profile score at 2 points for this question
        Object.keys(qScores).forEach(profile => {
          scores[profile] += Math.min(qScores[profile], 2);
        });

      } else {
        // Single choice question - add weights directly
        qResponses.forEach(response => {
          if (response.weights) {
            Object.keys(response.weights).forEach(profile => {
              if (scores.hasOwnProperty(profile)) {
                scores[profile] += response.weights[profile] || 0;
              }
            });
          }
        });
      }
    });

    // Determine primary profile (highest score with tie-breaker)
    const primary = determinePrimaryProfile(scores);

    // Calculate total score
    const totalScore = scores.hepatic + scores.lymphatic + scores.lipid;

    // Calculate recommended duration based on primary score
    const duration = calculateDuration(scores[primary]);

    // Generate result URL with persona
    const resultUrl = generateResultUrl(primary, persona);

    // Prepare result object
    const result = {
      primary: primary,
      scores: scores,
      totalScore: totalScore,
      primaryScore: scores[primary],
      persona: persona,
      duration: duration,
      resultUrl: resultUrl,
      profileName: getProfileName(primary),
      personaName: persona ? getPersonaName(persona) : null
    };

    // Debug logging
    if (window.location.search.includes('debug')) {
      console.log('=== Quiz Scoring Results ===');
      console.log('Responses:', responses);
      console.log('Scores:', scores);
      console.log('Total Score:', totalScore);
      console.log('Primary Profile:', primary);
      console.log('Primary Score:', scores[primary]);
      console.log('Recommended Duration:', duration.months, 'mois (' + duration.label + ')');
      console.log('Persona:', persona);
      console.log('Result URL:', resultUrl);
      console.log('==========================');
    }

    return result;
  };

  /**
   * Determine primary profile based on new simplified logic
   *
   * LOGIC:
   * 1. SI Score Hépatique ≥ 6 → HÉPATIQUE
   * 2. SINON SI Score Lymphatique > Score Lipidique → LYMPHATIQUE
   * 3. SINON SI Score Lipidique > Score Lymphatique → LIPIDIQUE
   * 4. SINON (égalité) → LYMPHATIQUE
   *
   * @param {Object} scores - Scores for each profile
   * @returns {string} Primary profile name
   */
  function determinePrimaryProfile(scores) {
    // Priority rule: Hepatic score ≥ 6 automatically wins
    if (scores.hepatic >= 6) {
      return 'hepatic';
    }

    // Compare lymphatic vs lipid
    if (scores.lymphatic > scores.lipid) {
      return 'lymphatic';
    } else if (scores.lipid > scores.lymphatic) {
      return 'lipid';
    } else {
      // Tie: default to lymphatic
      return 'lymphatic';
    }
  }

  /**
   * Calculate recommended cure duration based on primary profile score
   *
   * LOGIC:
   * - Score 0-4: 1 month (Cure Découverte) - Light imbalance
   * - Score 5-7: 2 months (Cure Standard) - Moderate imbalance
   * - Score 8-10: 3 months (Cure Intensive) - Significant imbalance
   *
   * @param {number} primaryScore - Score of the dominant profile (0-10)
   * @returns {Object} Duration object with months and label
   */
  function calculateDuration(primaryScore) {
    if (primaryScore >= 8) {
      return {
        months: 3,
        label: 'Cure Intensive',
        level: 'intensive'
      };
    } else if (primaryScore >= 5) {
      return {
        months: 2,
        label: 'Cure Standard',
        level: 'standard'
      };
    } else {
      return {
        months: 1,
        label: 'Cure Découverte',
        level: 'discovery'
      };
    }
  }

  /**
   * Generate result page URL with query parameters
   * Format: /pages/metabolic-quiz-result?profile={profile}&persona={persona}
   *
   * @param {string} profile - Primary profile
   * @param {string} persona - User persona (optional)
   * @returns {string} Full URL to result page with query params
   */
  function generateResultUrl(profile, persona) {
    // Get result page URL from section settings or use default
    const resultPageUrl = window.quizConfig?.resultPageUrl || '/pages/metabolic-quiz-result';

    // Build URL with query parameters
    let url = `${resultPageUrl}?profile=${profile}`;

    // Add persona if present
    if (persona) {
      url += `&persona=${persona}`;
    }

    return url;
  }

  /**
   * Get localized profile name
   * @param {string} profile - Profile key
   * @returns {string} Localized profile name
   */
  function getProfileName(profile) {
    const names = window.quizConfig?.profileNames || {
      hepatic: 'Hépatique',
      lymphatic: 'Lymphatique',
      lipid: 'Lipidique'
    };

    return names[profile] || profile;
  }

  /**
   * Get localized persona name
   * @param {string} persona - Persona key
   * @returns {string} Localized persona name
   */
  function getPersonaName(persona) {
    const names = window.quizConfig?.personaNames || {
      emma: 'Emma (Ventre plat)',
      clara: 'Clara (Légèreté)',
      julie: 'Julie (Éclat)',
      marc: 'Marc (Digestion)'
    };

    return names[persona] || persona;
  }

  /**
   * Get default result (fallback)
   * @returns {Object} Default result object
   */
  function getDefaultResult() {
    return {
      primary: 'hepatic',
      scores: { hepatic: 0, lymphatic: 0, lipid: 0 },
      totalScore: 0,
      primaryScore: 0,
      persona: null,
      duration: { months: 1, label: 'Cure Découverte', level: 'discovery' },
      resultUrl: '/pages/metabolic-quiz-result?profile=hepatic',
      profileName: 'Hépatique',
      personaName: null
    };
  }

  /**
   * Expose scoring debug function
   */
  window.debugScoring = function(responses) {
    const result = window.calculateProfile(responses);
    console.table({
      'Profil Hépatique': result.scores.hepatic,
      'Profil Lymphatique': result.scores.lymphatic,
      'Profil Lipidique': result.scores.lipid,
      'Score Total': result.totalScore,
      'Score Primaire': result.primaryScore
    });
    console.log('Profil principal:', result.profileName);
    console.log('Durée recommandée:', result.duration.months, 'mois (' + result.duration.label + ')');
    console.log('Persona:', result.personaName);
    console.log('URL de résultat:', result.resultUrl);
    return result;
  };


})();
